import { DELAY_TYPE } from '@lighthouse/core'

type Timer = ReturnType<typeof setTimeout>

type Deferred = {
    resolve: () => void
    reject: (e: unknown) => void
}

type DebounceState = {
    timer?: Timer
    task: () => Promise<void>
    deferred: Deferred[]
}

type ThrottlePending = {
    task: () => Promise<void>
    deferred: Deferred[]
}

type ThrottleState = {
    lastRun: number
    timer?: Timer
    pending?: ThrottlePending
}

/**
 * A small in-memory controller for event delay policies (debounce / throttle).
 *
 * - debounce: timer resets; latest task wins; all callers' promises resolve after the latest run finishes.
 * - throttle: leading edge runs immediately; trailing edge runs once at window end; latest task wins; all callers' promises resolve after the trailing run finishes.
 */
export class DelayRunController {
    private readonly debounceMap = new Map<string, DebounceState>()
    private readonly throttleMap = new Map<string, ThrottleState>()

    dispose() {
        // Important: callers may be awaiting the returned Promise.
        // On dispose(), we must not leave them hanging forever.
        this.debounceMap.forEach(v => {
            if (v.timer) {
                clearTimeout(v.timer)
            }
            v.deferred.forEach(d => d.resolve())
        })
        this.throttleMap.forEach(v => {
            if (v.timer) {
                clearTimeout(v.timer)
            }
            v.pending?.deferred.forEach(d => d.resolve())
        })
        this.debounceMap.clear()
        this.throttleMap.clear()
    }

    run(key: string, delayType: DELAY_TYPE | undefined, delayTime: number, task: () => Promise<void>): Promise<void> {
        if (delayType === DELAY_TYPE.DEBOUNCE) {
            return this.runDebounce(key, delayTime, task)
        }
        if (delayType === DELAY_TYPE.THROTTLE) {
            return this.runThrottle(key, delayTime, task)
        }
        return task()
    }

    private runDebounce(key: string, delayTime: number, task: () => Promise<void>): Promise<void> {
        if (delayTime <= 0) {
            return task()
        }

        return new Promise<void>((resolve, reject) => {
            const state: DebounceState = this.debounceMap.get(key) ?? { task, deferred: [] }
            state.task = task
            state.deferred.push({ resolve, reject })

            if (state.timer) {
                clearTimeout(state.timer)
            }

            state.timer = setTimeout(async () => {
                const cur = this.debounceMap.get(key)
                if (!cur) {
                    resolve()
                    return
                }
                this.debounceMap.delete(key)
                try {
                    await cur.task()
                    cur.deferred.forEach(d => d.resolve())
                } catch (e) {
                    cur.deferred.forEach(d => d.reject(e))
                }
            }, delayTime)

            this.debounceMap.set(key, state)
        })
    }

    private runThrottle(key: string, delayTime: number, task: () => Promise<void>): Promise<void> {
        if (delayTime <= 0) {
            return task()
        }

        const now = Date.now()
        const state: ThrottleState = this.throttleMap.get(key) ?? { lastRun: 0 }
        const elapsed = now - state.lastRun
        const remaining = delayTime - elapsed

        // leading edge
        if (state.lastRun === 0 || remaining <= 0) {
            state.lastRun = now
            this.throttleMap.set(key, state)
            return task()
        }

        // trailing edge (latest task wins)
        return new Promise<void>((resolve, reject) => {
            const pending: ThrottlePending = state.pending ?? { task, deferred: [] }
            pending.task = task
            pending.deferred.push({ resolve, reject })
            state.pending = pending

            if (!state.timer) {
                state.timer = setTimeout(async () => {
                    const cur = this.throttleMap.get(key)
                    if (!cur) {
                        resolve()
                        return
                    }

                    cur.timer = undefined
                    cur.lastRun = Date.now()
                    const p = cur.pending
                    cur.pending = undefined
                    this.throttleMap.set(key, cur)

                    if (!p) {
                        return
                    }

                    try {
                        await p.task()
                        p.deferred.forEach(d => d.resolve())
                    } catch (e) {
                        p.deferred.forEach(d => d.reject(e))
                    }
                }, remaining)
            }

            this.throttleMap.set(key, state)
        })
    }
}
